import { useState, useEffect } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { 
  Plus, Edit, Trash2, Power, PowerOff, Calendar, Users, 
  Sparkles, Crown, Gift, Zap, Star, Flame, Heart, Megaphone,
  Eye, EyeOff, ExternalLink, Upload, Video, Image as ImageIcon,
  Clock, Play, Settings, BarChart3, MousePointer, TrendingUp, X,
  Smartphone, Monitor, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format } from 'date-fns';

// Use same-origin API path on production custom domains
const API_BASE = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const ICONS = [
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'crown', label: 'Crown', icon: Crown },
  { value: 'gift', label: 'Gift', icon: Gift },
  { value: 'zap', label: 'Zap', icon: Zap },
  { value: 'star', label: 'Star', icon: Star },
  { value: 'fire', label: 'Fire', icon: Flame },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'megaphone', label: 'Megaphone', icon: Megaphone },
];

const BANNER_TYPES = [
  { value: 'styled', label: 'Styled Banner', icon: Sparkles, desc: 'Icon + gradient + text' },
  { value: 'image', label: 'Image Banner', icon: ImageIcon, desc: 'Upload custom image' },
  { value: 'video', label: 'Video Banner', icon: Video, desc: 'Upload or embed video' },
];

const ACTIONS = [
  { value: 'link', label: 'External Link' },
  { value: 'dismiss', label: 'Dismiss Only' },
];

const B2BPromoCampaignManager = ({ theme }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedCampaignAnalytics, setSelectedCampaignAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mobilePreview, setMobilePreview] = useState(false);
  
  const [formData, setFormData] = useState({
    banner_type: 'styled',
    title: '',
    subtitle: '',
    description: '',
    cta_text: 'Learn More',
    cta_link: '',
    cta_action: 'link',
    icon: 'sparkles',
    gradient_from: theme?.primary_color || '#df1283',
    gradient_to: theme?.secondary_color || '#FF7901',
    image_url: '',
    video_url: '',
    video_type: 'upload',
    is_active: false,
    start_date: '',
    end_date: '',
    priority: 0,
    allow_dismiss: true,
  });

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/promos`, {
        withCredentials: true
      });
      setCampaigns(res.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/promo-analytics`, {
        withCredentials: true
      });
      setAnalyticsData(res.data.campaigns || []);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchCampaignAnalytics = async (campaignId) => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API_BASE}/api/client-admin/promo-analytics/${campaignId}`, {
        withCredentials: true
      });
      setSelectedCampaignAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load campaign analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingCampaign) {
        await axios.put(
          `${API_BASE}/api/client-admin/promos/${editingCampaign.id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Campaign updated!');
      } else {
        await axios.post(
          `${API_BASE}/api/client-admin/promos`,
          formData,
          { withCredentials: true }
        );
        toast.success('Campaign created!');
      }
      
      setShowEditor(false);
      setEditingCampaign(null);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    
    try {
      await axios.delete(`${API_BASE}/api/client-admin/promos/${id}`, {
        withCredentials: true
      });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.post(`${API_BASE}/api/client-admin/promos/${id}/toggle`, {}, {
        withCredentials: true
      });
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to toggle campaign');
    }
  };

  const handleMediaUpload = async (e, mediaType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = mediaType === 'video';
    const validTypes = isVideo 
      ? ['video/mp4', 'video/webm', 'video/ogg']
      : ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for video, 5MB for image

    if (!validTypes.includes(file.type)) {
      toast.error(`Please upload a valid ${mediaType} file`);
      return;
    }

    if (file.size > maxSize) {
      toast.error(`${isVideo ? 'Video' : 'Image'} must be under ${isVideo ? '50MB' : '5MB'}`);
      return;
    }

    setUploadingMedia(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      // Use the same upload endpoint as the main app
      const res = await axios.post(`${API_BASE}/api/upload-media`, uploadFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.url) {
        if (isVideo) {
          setFormData(prev => ({ ...prev, video_url: res.data.url }));
        } else {
          setFormData(prev => ({ ...prev, image_url: res.data.url }));
        }
        toast.success(`${isVideo ? 'Video' : 'Image'} uploaded!`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Failed to upload ${mediaType}`);
    } finally {
      setUploadingMedia(false);
    }
  };

  const resetForm = () => {
    setFormData({
      banner_type: 'styled',
      title: '',
      subtitle: '',
      description: '',
      cta_text: 'Learn More',
      cta_link: '',
      cta_action: 'link',
      icon: 'sparkles',
      gradient_from: theme?.primary_color || '#df1283',
      gradient_to: theme?.secondary_color || '#FF7901',
      image_url: '',
      video_url: '',
      video_type: 'upload',
      is_active: false,
      start_date: '',
      end_date: '',
      priority: 0,
      allow_dismiss: true,
    });
  };

  const openEditor = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        banner_type: campaign.banner_type || 'styled',
        title: campaign.title || '',
        subtitle: campaign.subtitle || '',
        description: campaign.description || '',
        cta_text: campaign.cta_text || 'Learn More',
        cta_link: campaign.cta_link || '',
        cta_action: campaign.cta_action || 'link',
        icon: campaign.icon || 'sparkles',
        gradient_from: campaign.gradient_from || theme?.primary_color || '#df1283',
        gradient_to: campaign.gradient_to || theme?.secondary_color || '#FF7901',
        image_url: campaign.image_url || '',
        video_url: campaign.video_url || '',
        video_type: campaign.video_type || 'upload',
        is_active: campaign.is_active || false,
        start_date: campaign.start_date || '',
        end_date: campaign.end_date || '',
        priority: campaign.priority || 0,
        allow_dismiss: campaign.allow_dismiss !== false,
      });
    } else {
      setEditingCampaign(null);
      resetForm();
    }
    setShowEditor(true);
  };

  const getIconComponent = (iconName) => {
    const iconObj = ICONS.find(i => i.value === iconName);
    return iconObj ? iconObj.icon : Sparkles;
  };

  // Theme-aware styles
  const cardStyle = {
    background: theme?.background_secondary || '#1E293B',
    border: `1px solid ${theme?.border_color || '#334155'}`,
  };

  const inputStyle = {
    background: theme?.background_primary || '#0F172A',
    border: `1px solid ${theme?.border_color || '#334155'}`,
    color: theme?.text_primary || '#F8FAFC',
  };

  const labelStyle = {
    color: theme?.text_primary || '#F8FAFC',
  };

  const subtextStyle = {
    color: theme?.text_secondary || '#94A3B8',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div 
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: theme?.primary_color || '#06B6D4', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: theme?.text_primary }}>
            <Megaphone className="w-6 h-6" style={{ color: theme?.primary_color }} />
            Promotional Campaigns
          </h2>
          <p className="text-sm mt-1" style={subtextStyle}>
            Create and manage promotional banners for your users
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { setShowAnalytics(true); fetchAnalytics(); }}
            style={{ 
              borderColor: theme?.border_color,
              color: theme?.text_primary
            }}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          <Button
            onClick={() => openEditor()}
            style={{
              background: `linear-gradient(135deg, ${theme?.primary_color || '#df1283'}, ${theme?.secondary_color || '#FF7901'})`,
              color: '#ffffff'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <div 
          className="rounded-xl p-12 text-center"
          style={cardStyle}
        >
          <Megaphone className="w-16 h-16 mx-auto mb-4" style={subtextStyle} />
          <h3 className="text-lg font-semibold mb-2" style={labelStyle}>No campaigns yet</h3>
          <p style={subtextStyle}>Create your first promotional campaign to engage your users</p>
          <Button
            className="mt-4"
            onClick={() => openEditor()}
            style={{
              background: `linear-gradient(135deg, ${theme?.primary_color || '#df1283'}, ${theme?.secondary_color || '#FF7901'})`,
              color: '#ffffff'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Campaign
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {campaigns.map((campaign) => {
            const IconComponent = getIconComponent(campaign.icon);
            return (
              <div 
                key={campaign.id}
                className="rounded-xl p-5 transition-all hover:scale-[1.01]"
                style={cardStyle}
              >
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div 
                    className="w-24 h-24 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
                    style={{
                      background: campaign.banner_type === 'styled' 
                        ? `linear-gradient(135deg, ${campaign.gradient_from || theme?.primary_color}, ${campaign.gradient_to || theme?.secondary_color})`
                        : (theme?.background_primary || '#0F172A')
                    }}
                  >
                    {campaign.banner_type === 'image' && campaign.image_url ? (
                      <img src={campaign.image_url} alt="" className="w-full h-full object-cover" />
                    ) : campaign.banner_type === 'video' && campaign.video_url ? (
                      <Video className="w-10 h-10 text-white/70" />
                    ) : (
                      <IconComponent className="w-10 h-10 text-white" />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-lg" style={labelStyle}>{campaign.title}</h3>
                      <span 
                        className="text-xs px-2.5 py-1 rounded-full font-medium"
                        style={{
                          backgroundColor: campaign.is_active 
                            ? `${theme?.success_color || '#10B981'}20` 
                            : `${theme?.text_secondary || '#94A3B8'}20`,
                          color: campaign.is_active 
                            ? (theme?.success_color || '#10B981')
                            : (theme?.text_secondary || '#94A3B8')
                        }}
                      >
                        {campaign.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span 
                        className="text-xs px-2.5 py-1 rounded-full capitalize"
                        style={{
                          backgroundColor: `${theme?.primary_color || '#06B6D4'}20`,
                          color: theme?.primary_color || '#06B6D4'
                        }}
                      >
                        {campaign.banner_type}
                      </span>
                    </div>
                    
                    {campaign.subtitle && (
                      <p className="text-sm mb-2" style={subtextStyle}>{campaign.subtitle}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs" style={subtextStyle}>
                      {campaign.cta_text && (
                        <span className="flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          CTA: {campaign.cta_text}
                        </span>
                      )}
                      {campaign.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {format(new Date(campaign.start_date), 'MMM d')}
                          {campaign.end_date && ` - ${format(new Date(campaign.end_date), 'MMM d')}`}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {campaign.views || 0} views
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="w-3 h-3" />
                        {campaign.clicks || 0} clicks
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(campaign.id)}
                      title={campaign.is_active ? 'Deactivate' : 'Activate'}
                      style={{ color: campaign.is_active ? (theme?.success_color || '#10B981') : (theme?.text_secondary || '#94A3B8') }}
                    >
                      {campaign.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditor(campaign)}
                      title="Edit"
                      style={{ color: theme?.primary_color || '#06B6D4' }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(campaign.id)}
                      title="Delete"
                      style={{ color: theme?.error_color || '#EF4444' }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Campaign Editor Modal */}
      {showEditor && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowEditor(false)}
        >
          <div 
            className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl"
            style={cardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between" 
              style={{ 
                background: theme?.background_secondary,
                borderColor: theme?.border_color 
              }}
            >
              <h3 className="text-lg font-semibold" style={labelStyle}>
                {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
              </h3>
              <button 
                onClick={() => setShowEditor(false)}
                className="p-2 rounded-full hover:bg-white/10"
                style={{ color: theme?.text_secondary }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Banner Type Selection */}
              <div>
                <label className="block text-sm font-medium mb-3" style={labelStyle}>Banner Type</label>
                <div className="grid grid-cols-3 gap-3">
                  {BANNER_TYPES.map(type => {
                    const TypeIcon = type.icon;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, banner_type: type.value }))}
                        className="p-4 rounded-xl text-left transition-all"
                        style={{
                          background: formData.banner_type === type.value 
                            ? `${theme?.primary_color}20` 
                            : (theme?.background_primary || '#0F172A'),
                          border: `2px solid ${formData.banner_type === type.value 
                            ? theme?.primary_color 
                            : theme?.border_color}`
                        }}
                      >
                        <TypeIcon className="w-6 h-6 mb-2" style={{ color: theme?.primary_color }} />
                        <div className="font-medium" style={labelStyle}>{type.label}</div>
                        <div className="text-xs" style={subtextStyle}>{type.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {/* Left Column - Form Fields */}
                <div className="space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={labelStyle}>Title *</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Special Offer!"
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={labelStyle}>Subtitle</label>
                    <Input
                      value={formData.subtitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                      placeholder="Brief description"
                      style={inputStyle}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium mb-1" style={labelStyle}>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Longer description (optional)"
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg"
                      style={inputStyle}
                    />
                  </div>

                  {/* Styled Banner Options */}
                  {formData.banner_type === 'styled' && (
                    <>
                      {/* Icon */}
                      <div>
                        <label className="block text-sm font-medium mb-2" style={labelStyle}>Icon</label>
                        <div className="flex flex-wrap gap-2">
                          {ICONS.map(({ value, icon: Icon }) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, icon: value }))}
                              className="p-2 rounded-lg transition-all"
                              style={{
                                background: formData.icon === value 
                                  ? theme?.primary_color 
                                  : (theme?.background_primary || '#0F172A'),
                                border: `1px solid ${theme?.border_color}`
                              }}
                            >
                              <Icon className="w-5 h-5" style={{ color: formData.icon === value ? '#fff' : theme?.text_secondary }} />
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Gradient Colors */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium mb-1" style={labelStyle}>Gradient From</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.gradient_from}
                              onChange={(e) => setFormData(prev => ({ ...prev, gradient_from: e.target.value }))}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={formData.gradient_from}
                              onChange={(e) => setFormData(prev => ({ ...prev, gradient_from: e.target.value }))}
                              className="flex-1"
                              style={inputStyle}
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1" style={labelStyle}>Gradient To</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={formData.gradient_to}
                              onChange={(e) => setFormData(prev => ({ ...prev, gradient_to: e.target.value }))}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <Input
                              value={formData.gradient_to}
                              onChange={(e) => setFormData(prev => ({ ...prev, gradient_to: e.target.value }))}
                              className="flex-1"
                              style={inputStyle}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Image Upload */}
                  {formData.banner_type === 'image' && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={labelStyle}>Banner Image</label>
                      {formData.image_url ? (
                        <div className="relative">
                          <img src={formData.image_url} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                            className="absolute top-2 right-2 p-1 rounded-full bg-black/50 hover:bg-black/70"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ) : (
                        <label 
                          className="flex flex-col items-center justify-center w-full h-40 rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                          style={{ border: `2px dashed ${theme?.border_color}` }}
                        >
                          <Upload className="w-8 h-8 mb-2" style={subtextStyle} />
                          <span className="text-sm" style={subtextStyle}>
                            {uploadingMedia ? 'Uploading...' : 'Click to upload image'}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleMediaUpload(e, 'image')}
                            disabled={uploadingMedia}
                          />
                        </label>
                      )}
                    </div>
                  )}

                  {/* Video Upload/Embed */}
                  {formData.banner_type === 'video' && (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={formData.video_type === 'upload' ? 'default' : 'outline'}
                          onClick={() => setFormData(prev => ({ ...prev, video_type: 'upload' }))}
                          size="sm"
                          style={formData.video_type === 'upload' ? {
                            background: theme?.primary_color,
                            color: '#fff'
                          } : {
                            borderColor: theme?.border_color,
                            color: theme?.text_secondary
                          }}
                        >
                          Upload
                        </Button>
                        <Button
                          type="button"
                          variant={formData.video_type === 'embed' ? 'default' : 'outline'}
                          onClick={() => setFormData(prev => ({ ...prev, video_type: 'embed' }))}
                          size="sm"
                          style={formData.video_type === 'embed' ? {
                            background: theme?.primary_color,
                            color: '#fff'
                          } : {
                            borderColor: theme?.border_color,
                            color: theme?.text_secondary
                          }}
                        >
                          Embed Code
                        </Button>
                      </div>
                      
                      {formData.video_type === 'embed' ? (
                        <div className="space-y-2">
                          <textarea
                            value={formData.video_url}
                            onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                            placeholder="Paste YouTube/Vimeo URL or full embed code (iframe)"
                            rows={4}
                            className="w-full px-3 py-2 rounded-lg text-sm font-mono"
                            style={inputStyle}
                          />
                          <p className="text-xs" style={subtextStyle}>
                            Supports: YouTube URLs, Vimeo URLs, or full iframe embed codes
                          </p>
                        </div>
                      ) : (
                        <label 
                          className="flex flex-col items-center justify-center w-full h-32 rounded-lg cursor-pointer hover:opacity-80"
                          style={{ border: `2px dashed ${theme?.border_color}` }}
                        >
                          <Video className="w-8 h-8 mb-2" style={subtextStyle} />
                          <span className="text-sm" style={subtextStyle}>
                            {uploadingMedia ? 'Uploading...' : 'Click to upload video'}
                          </span>
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => handleMediaUpload(e, 'video')}
                            disabled={uploadingMedia}
                          />
                        </label>
                      )}
                    </div>
                  )}

                  {/* CTA */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={labelStyle}>CTA Button Text</label>
                      <Input
                        value={formData.cta_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                        placeholder="Learn More"
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={labelStyle}>CTA Action</label>
                      <select
                        value={formData.cta_action}
                        onChange={(e) => setFormData(prev => ({ ...prev, cta_action: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg"
                        style={inputStyle}
                      >
                        {ACTIONS.map(a => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {formData.cta_action === 'link' && (
                    <div>
                      <label className="block text-sm font-medium mb-1" style={labelStyle}>CTA Link URL</label>
                      <Input
                        value={formData.cta_link}
                        onChange={(e) => setFormData(prev => ({ ...prev, cta_link: e.target.value }))}
                        placeholder="https://..."
                        style={inputStyle}
                      />
                    </div>
                  )}

                  {/* Schedule */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={labelStyle}>Start Date</label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={labelStyle}>End Date</label>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                        style={inputStyle}
                      />
                    </div>
                  </div>

                  {/* Active Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: theme?.background_primary }}>
                    <div>
                      <div className="font-medium" style={labelStyle}>Active</div>
                      <div className="text-xs" style={subtextStyle}>Show this campaign to users</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                      style={{ color: formData.is_active ? theme?.success_color : theme?.text_secondary }}
                    >
                      {formData.is_active ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
                    </button>
                  </div>
                </div>

                {/* Right Column - Preview */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium" style={labelStyle}>Preview</label>
                    <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: theme?.background_primary }}>
                      <button
                        type="button"
                        onClick={() => setMobilePreview(false)}
                        className="p-1.5 rounded"
                        style={{ background: !mobilePreview ? theme?.primary_color : 'transparent' }}
                      >
                        <Monitor className="w-4 h-4" style={{ color: !mobilePreview ? '#fff' : theme?.text_secondary }} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setMobilePreview(true)}
                        className="p-1.5 rounded"
                        style={{ background: mobilePreview ? theme?.primary_color : 'transparent' }}
                      >
                        <Smartphone className="w-4 h-4" style={{ color: mobilePreview ? '#fff' : theme?.text_secondary }} />
                      </button>
                    </div>
                  </div>
                  
                  <div 
                    className={`rounded-xl overflow-hidden mx-auto ${mobilePreview ? 'max-w-[320px]' : ''}`}
                    style={{ 
                      background: formData.banner_type === 'styled'
                        ? `linear-gradient(135deg, ${formData.gradient_from}, ${formData.gradient_to})`
                        : (theme?.background_primary || '#0F172A'),
                      minHeight: '200px'
                    }}
                  >
                    {formData.banner_type === 'image' && formData.image_url ? (
                      <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" />
                    ) : formData.banner_type === 'video' && formData.video_url ? (
                      <div className="relative w-full h-48">
                        {/* Check if it's an iframe embed code - sanitize to prevent XSS */}
                        {formData.video_url.includes('<iframe') ? (
                          <div 
                            className="w-full h-full"
                            dangerouslySetInnerHTML={{ 
                              __html: DOMPurify.sanitize(
                                formData.video_url.replace(/<iframe/g, '<iframe style="width:100%;height:100%;border:0;"'),
                                { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'style'] }
                              )
                            }} 
                          />
                        ) : formData.video_url.includes('youtube.com') || formData.video_url.includes('youtu.be') ? (
                          /* YouTube URL - convert to embed */
                          <iframe
                            src={`https://www.youtube.com/embed/${
                              formData.video_url.includes('youtu.be') 
                                ? formData.video_url.split('youtu.be/')[1]?.split('?')[0]
                                : formData.video_url.split('v=')[1]?.split('&')[0]
                            }`}
                            className="w-full h-full"
                            allowFullScreen
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          />
                        ) : formData.video_url.includes('vimeo.com') ? (
                          /* Vimeo URL - convert to embed */
                          <iframe
                            src={`https://player.vimeo.com/video/${formData.video_url.split('vimeo.com/')[1]?.split('?')[0]}`}
                            className="w-full h-full"
                            allowFullScreen
                            allow="autoplay; fullscreen; picture-in-picture"
                          />
                        ) : (
                          /* Direct video file */
                          <video src={formData.video_url} className="w-full h-full object-cover" controls />
                        )}
                      </div>
                    ) : (
                      <div className="p-6 flex flex-col items-center justify-center text-center text-white min-h-[200px]">
                        {(() => {
                          const IconComp = getIconComponent(formData.icon);
                          return <IconComp className="w-12 h-12 mb-4" />;
                        })()}
                        <h4 className="text-xl font-bold mb-1">{formData.title || 'Your Title Here'}</h4>
                        {formData.subtitle && <p className="text-sm opacity-90 mb-3">{formData.subtitle}</p>}
                        {formData.cta_text && (
                          <button className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-full text-sm font-medium">
                            {formData.cta_text}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t" style={{ borderColor: theme?.border_color }}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditor(false)}
                  style={{ borderColor: theme?.border_color, color: theme?.text_secondary }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  style={{
                    background: `linear-gradient(135deg, ${theme?.primary_color || '#df1283'}, ${theme?.secondary_color || '#FF7901'})`,
                    color: '#ffffff'
                  }}
                >
                  {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {showAnalytics && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setShowAnalytics(false); setSelectedCampaignAnalytics(null); }}
        >
          <div 
            className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl"
            style={cardStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 px-6 py-4 border-b flex items-center justify-between"
              style={{ background: theme?.background_secondary, borderColor: theme?.border_color }}
            >
              <h3 className="text-lg font-semibold flex items-center gap-2" style={labelStyle}>
                <BarChart3 className="w-5 h-5" style={{ color: theme?.primary_color }} />
                Campaign Analytics
              </h3>
              <button 
                onClick={() => { setShowAnalytics(false); setSelectedCampaignAnalytics(null); }}
                className="p-2 rounded-full hover:bg-white/10"
                style={{ color: theme?.text_secondary }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {loadingAnalytics ? (
                <div className="flex items-center justify-center py-12">
                  <div 
                    className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: theme?.primary_color, borderTopColor: 'transparent' }}
                  />
                </div>
              ) : selectedCampaignAnalytics ? (
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCampaignAnalytics(null)}
                    style={{ color: theme?.text_secondary }}
                  >
                    ← Back to all campaigns
                  </Button>
                  <h4 className="text-lg font-semibold" style={labelStyle}>{selectedCampaignAnalytics.title}</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl text-center" style={{ background: theme?.background_primary }}>
                      <Eye className="w-6 h-6 mx-auto mb-2" style={{ color: theme?.primary_color }} />
                      <div className="text-2xl font-bold" style={labelStyle}>{selectedCampaignAnalytics.views || 0}</div>
                      <div className="text-xs" style={subtextStyle}>Views</div>
                    </div>
                    <div className="p-4 rounded-xl text-center" style={{ background: theme?.background_primary }}>
                      <MousePointer className="w-6 h-6 mx-auto mb-2" style={{ color: theme?.primary_color }} />
                      <div className="text-2xl font-bold" style={labelStyle}>{selectedCampaignAnalytics.clicks || 0}</div>
                      <div className="text-xs" style={subtextStyle}>Clicks</div>
                    </div>
                    <div className="p-4 rounded-xl text-center" style={{ background: theme?.background_primary }}>
                      <TrendingUp className="w-6 h-6 mx-auto mb-2" style={{ color: theme?.primary_color }} />
                      <div className="text-2xl font-bold" style={labelStyle}>
                        {selectedCampaignAnalytics.views > 0 
                          ? ((selectedCampaignAnalytics.clicks / selectedCampaignAnalytics.views) * 100).toFixed(1) 
                          : 0}%
                      </div>
                      <div className="text-xs" style={subtextStyle}>CTR</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {analyticsData && analyticsData.length > 0 ? (
                    analyticsData.map(campaign => (
                      <div 
                        key={campaign.id}
                        className="p-4 rounded-xl flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                        style={{ background: theme?.background_primary }}
                        onClick={() => fetchCampaignAnalytics(campaign.id)}
                      >
                        <div>
                          <div className="font-medium" style={labelStyle}>{campaign.title}</div>
                          <div className="text-xs" style={subtextStyle}>
                            {campaign.views || 0} views • {campaign.clicks || 0} clicks
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4" style={subtextStyle} />
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8" style={subtextStyle}>
                      No analytics data available yet
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default B2BPromoCampaignManager;
